import { EventEmitter } from 'events';

import { FetchConfig, ResourceFetch, ResourceFetchTemplate } from '../../meta/types/api';
import { ContentType } from '../../meta/types/content-type';
import { HttpMethod, HttpStatus } from '../../meta/types/http';
import Auth from '../auth';
import { toFormData } from '../form';
import { injectParameters } from '../urls';

type Mappings = {
  [contentType: string]: (resp: any) => any;
};

const defaultHeaders: any = {};

const contentTypeMappings: Mappings = {
  [ContentType.JSON]: resp => resp.json(),
  [ContentType.JS]: resp => resp.json(),
  [ContentType.XML]: resp => resp.text(),
  [ContentType.TEXT]: resp => resp.text(),
  [ContentType.CSV]: resp => resp.text(),
  [ContentType.HTML]: resp => resp.text(),
  'application/json;charset=utf-8': resp => resp.json(),
  'application/json; charset=UTF-8': resp => resp.json(),
};

export type EndpointConfig = {
  authenticated?: boolean;
  endpointHeaders?: HeadersInit;
  withCredentials?: boolean;
};

const DEFAULT_ENDPOINT_CONFIG = {
  authenticated: true,
  withCredentials: false,
  endpointHeaders: {},
};

const CONTENT_TYPE_HEADER = 'Content-Type';

class Api extends EventEmitter {
  GET = this.makeMethod(HttpMethod.GET);
  POST = this.makeMethod(HttpMethod.POST, true);
  PUT = this.makeMethod(HttpMethod.PUT, true);
  DELETE = this.makeMethod(HttpMethod.DELETE);
  PATCH = this.makeMethod(HttpMethod.PATCH, true);

  setDefaultHeader = (key: string, value: string) => {
    defaultHeaders[key] = value;
  };

  makeMethod(method: HttpMethod, hasBody = false): ResourceFetchTemplate<any, any> {
    return (
      urlTemplate: string,
      endpointConfig: EndpointConfig = DEFAULT_ENDPOINT_CONFIG,
      contentType: ContentType = ContentType.JSON,
    ): ResourceFetch<any, any> => {
      return (data: any = undefined, fetchConfig: FetchConfig = {}): Promise<any> => {
        const url = injectParameters(urlTemplate, data, hasBody);
        const { authenticated, endpointHeaders } = endpointConfig;
        const headers: any = {
          Accept: contentType,
          ...defaultHeaders,
          ...endpointHeaders,
        };

        let body = null;
        if (hasBody && data) {
          if (typeof data === 'string') {
            body = data;
            headers[CONTENT_TYPE_HEADER] = ContentType.TEXT;
          } else if (data instanceof FormData || fetchConfig.asFormData) {
            body = toFormData(data);
            headers[CONTENT_TYPE_HEADER] = ContentType.FORM_DATA;
          } else {
            body = JSON.stringify(data);
            headers[CONTENT_TYPE_HEADER] = ContentType.JSON;
          }
        }

        if (authenticated && !headers.Authorization) {
          headers.Authorization = Auth.getAuthorizationHeader();
        }

        return this.makeRequest(method, url, headers, body, endpointConfig.withCredentials);
      };
    };
  }
  makeRequest(
    method: HttpMethod,
    url: string,
    headers: HeadersInit,
    body: string | FormData | null,
    withCredentials = false,
  ): Promise<any> {
    return fetch(url, {
      method,
      headers,
      body,
      credentials: withCredentials ? 'include' : 'omit',
    })
      .then((response: any) => {
        this.emit(`${response.status}`, url);
        const contentType = response.headers.get(CONTENT_TYPE_HEADER);
        const mappingFunction = contentTypeMappings[contentType] || (resp => resp.text());
        return new Promise(resolve => resolve(mappingFunction(response)))
          .catch(err => {
            return Promise.reject({
              type: 'NetworkError',
              status: response.status,
              message: err,
            });
          })
          .then((responseBody: any) => {
            if (response.ok) {
              return responseBody;
            }

            if (response.status >= HttpStatus.SERVER_ERROR) {
              return Promise.reject({
                type: 'ServerError',
                status: response.status,
                body: responseBody,
              });
            }
            if (response.status < HttpStatus.SERVER_ERROR) {
              return Promise.reject({
                type: 'ApplicationError',
                status: response.status,
                body: responseBody,
              });
            }
          });
      })
      .catch((err: ApiError<any>) => {
        return err.type
          ? Promise.reject(err)
          : Promise.reject({
              type: 'ConnectionRefused',
              status: HttpStatus.SERVER_ERROR,
              body: 'Check your internet connection',
              err,
            });
      });
  }
}

export interface ApiError<T> {
  type: 'ApplicationError' | 'ServerError' | 'NetworkError';
  status: number;
  body: T;
}

const instance = new Api();

export default instance;
